import { useEffect, useState } from 'react'
import { Chip } from '@mui/material'
import { CheckCircle as ApproveIcon } from '@mui/icons-material'
import { ApiError, northStarApi, NorthStarRecord } from '../../services/api'
import { isNorthStarClientApproved } from '../../utils/northStarApproval'

interface NorthStarApprovalStatusProps {
  clientId: string
  // The client-record mirror of the approval. They are not what this reads,
  // but a change to either is the signal that the North Star record changed.
  northStarApproved?: boolean
  northStarApprovedAt?: string
}

/**
 * The client's sign-off on the editor-built North Star, read from the North
 * Star record rather than client.northStarApproved: a legacy PDF approval sets
 * that mirror too, so on its own it cannot say whether the version live now is
 * the one the client approved.
 *
 * Renders nothing it cannot state: while loading, when the read fails (the
 * endpoint refuses staff who are not admins or managers), when there is no
 * record, and when it is unpublished.
 */
export default function NorthStarApprovalStatus({
  clientId,
  northStarApproved,
  northStarApprovedAt,
}: NorthStarApprovalStatusProps) {
  const [record, setRecord] = useState<NorthStarRecord | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const resp = await northStarApi.get(clientId)
        if (!cancelled) setRecord(resp?.northStar ?? null)
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 403)) {
          console.error('Failed to load the North Star approval status:', err)
        }
        if (!cancelled) setRecord(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [clientId, northStarApproved, northStarApprovedAt])

  if (!record?.isPublished) return null

  if (isNorthStarClientApproved(record)) {
    return (
      <Chip
        size="small"
        color="success"
        icon={<ApproveIcon />}
        label={
          record.clientApprovedAt
            ? `Approved by client · ${new Date(record.clientApprovedAt).toLocaleDateString()}`
            : 'Approved by client'
        }
      />
    )
  }

  return <Chip size="small" variant="outlined" label="Awaiting client approval" />
}
